import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Link } from 'react-router-dom';
import { Pagination } from '@/components/Pagination';
import { toast } from 'sonner';
import SelfCreateRequestModal from '@/components/SelfCreateRequestModal';

var STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  assigned: { label: 'Assigned', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  demo_scheduled: { label: 'Demo Scheduled', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  poc_in_progress: { label: 'POC In Progress', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400' },
};

var SLA_COLORS = { met: 'text-green-600', on_track: 'text-green-600', at_risk: 'text-amber-600', breached: 'text-red-600', 'n/a': 'text-slate-400' };
var SLA_ICONS = { met: 'M5 13l4 4L19 7', on_track: 'M5 13l4 4L19 7', at_risk: 'M12 9v2m0 4h.01M12 3a9 9 0 110 18 9 9 0 010-18z', breached: 'M6 18L18 6M6 6l12 12' };
var URGENCY_COLORS = { low: 'text-slate-500', medium: 'text-blue-600 dark:text-blue-400', high: 'text-orange-600 dark:text-orange-400', critical: 'text-red-600 dark:text-red-400 font-bold' };

function StatCard(props) {
  var isClickable = typeof props.onClick === 'function';
  var className = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-left w-full transition-all ' +
    (isClickable ? 'hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md cursor-pointer' : '');
  if (isClickable) {
    return (
      <button type="button" onClick={props.onClick} className={className} data-testid={'stat-' + props.testId} title={props.title || ('View ' + props.label + ' requests')}>
        <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{props.label}</p>
        <p className={'text-xl font-bold mt-0.5 ' + (props.color || 'text-slate-900 dark:text-white')}>{props.value}</p>
        {props.sub && <p className="text-[10px] text-slate-400 mt-0.5">{props.sub}</p>}
      </button>
    );
  }
  return (
    <div className={className} data-testid={'stat-' + props.testId}>
      <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{props.label}</p>
      <p className={'text-xl font-bold mt-0.5 ' + (props.color || 'text-slate-900 dark:text-white')}>{props.value}</p>
      {props.sub && <p className="text-[10px] text-slate-400 mt-0.5">{props.sub}</p>}
    </div>
  );
}

function SLABadge(props) {
  var sla = props.sla;
  if (!sla) return null;
  var status = props.type === 'response' ? sla.response_status : sla.resolution_status;
  var color = SLA_COLORS[status] || SLA_COLORS['n/a'];
  var iconPath = SLA_ICONS[status] || SLA_ICONS.met;
  var label = props.type === 'response' ? 'Resp' : 'Resol';
  return (
    <span className={'inline-flex items-center gap-0.5 text-[10px] font-medium ' + color} title={props.type + ' SLA: ' + status}>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath}/></svg>
      {label}
    </span>
  );
}

export default function PreSalesDashboard() {
  var { user } = useAuth();
  var canClaim = user && ['pre_sales_engineer', 'solutions_architect', 'admin', 'company_sales_head'].includes(user.role);
  var [stats, setStats] = useState(null);
  var [requests, setRequests] = useState([]);
  var [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, pages: 0 });
  var [statusFilter, setStatusFilter] = useState('');
  var [slaFilter, setSlaFilter] = useState('');  // '' | 'breached' | 'at_risk'
  var [expandedIds, setExpandedIds] = useState({}); // {reqId: true} — show requirements/remarks
  var [loading, setLoading] = useState(true);
  var [users, setUsers] = useState([]);
  var [workload, setWorkload] = useState([]);
  var [assignModal, setAssignModal] = useState(null);
  var [selectedAssignee, setSelectedAssignee] = useState('');
  var [outcomeModal, setOutcomeModal] = useState(null);
  var [outcomeValue, setOutcomeValue] = useState('');
  var [outcomeNotes, setOutcomeNotes] = useState('');
  var [activeTab, setActiveTab] = useState('requests');
  var [slaAlerts, setSlaAlerts] = useState(null);  // iteration 78 ticker
  var [showSelfCreate, setShowSelfCreate] = useState(null); // null | 'demo' | 'request'

  var fetchStats = useCallback(function() {
    return axios.get(API + '/presales/dashboard/stats').then(function(r) { setStats(r.data); }).catch(function() {});
  }, []);

  var fetchSlaAlerts = useCallback(function() {
    return axios.get(API + '/presales/alerts/sla').then(function(r) { setSlaAlerts(r.data); }).catch(function() {});
  }, []);

  var fetchRequests = useCallback(function(page, status) {
    var params = '?page=' + page + '&limit=15';
    if (status) params += '&status=' + status;
    return axios.get(API + '/presales' + params).then(function(r) {
      setRequests(r.data.data || []);
      setPagination(r.data.pagination || {});
    }).catch(function() {});
  }, []);

  var fetchUsers = useCallback(function() {
    return axios.get(API + '/users').then(function(r) {
      setUsers((r.data || []).filter(function(u) { return u.role === 'pre_sales_engineer' || u.role === 'solutions_architect'; }));
    }).catch(function() {});
  }, []);

  var fetchWorkload = useCallback(function() {
    return axios.get(API + '/presales/workload').then(function(r) { setWorkload(r.data || []); }).catch(function() {});
  }, []);

  useEffect(function() {
    setLoading(true);
    Promise.all([fetchStats(), fetchRequests(1, statusFilter), fetchUsers(), fetchWorkload(), fetchSlaAlerts()]).finally(function() { setLoading(false); });
    // Poll the SLA ticker every 60s so it stays fresh without a hard refresh.
    var iv = setInterval(function() { fetchSlaAlerts(); }, 60000);
    return function() { clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function() {
    fetchRequests(pagination.page, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  function extractError(err, fallback) {
    var detail = err && err.response && err.response.data && err.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0] && detail[0].msg) return detail[0].msg;
    return fallback;
  }

  function handleAssign() {
    if (!assignModal || !selectedAssignee) return;
    axios.put(API + '/presales/' + assignModal.id, { assigned_to: selectedAssignee }).then(function() {
      setAssignModal(null); setSelectedAssignee('');
      toast.success('Request assigned');
      fetchStats(); fetchRequests(pagination.page, statusFilter); fetchWorkload();
    }).catch(function(err) {
      toast.error(extractError(err, 'Failed to assign request. Please try again.'));
    });
  }

  function handleClaim(requestId) {
    axios.post(API + '/presales/' + requestId + '/claim').then(function(r) {
      toast.success('Claimed — added to your task list');
      fetchStats(); fetchRequests(pagination.page, statusFilter); fetchWorkload(); fetchSlaAlerts();
    }).catch(function(err) {
      toast.error(extractError(err, 'Failed to claim request. It may have been claimed by someone else already.'));
      fetchRequests(pagination.page, statusFilter);
    });
  }

  function handleReroute(req) {
    var defaultReason = 'Not my expertise area';
    var reason = window.prompt(
      'Reroute "' + (req.title || 'this request') + '" back to the queue?\n\nReason (required):',
      defaultReason
    );
    if (!reason || !reason.trim()) return;
    axios.post(API + '/presales/' + req.id + '/reroute', { reason: reason.trim() }).then(function() {
      toast.success('Request rerouted to the queue');
      fetchStats(); fetchRequests(pagination.page, statusFilter); fetchWorkload(); fetchSlaAlerts();
    }).catch(function(err) {
      toast.error(extractError(err, 'Failed to reroute request.'));
    });
  }

  function handleStatusChange(reqId, newStatus) {
    axios.put(API + '/presales/' + reqId, { status: newStatus }).then(function() {
      toast.success('Status updated');
      fetchStats(); fetchRequests(pagination.page, statusFilter);
    }).catch(function(err) {
      toast.error(extractError(err, 'Failed to change status. Please try again.'));
    });
  }

  function handleOutcome() {
    if (!outcomeModal || !outcomeValue) return;
    axios.put(API + '/presales/' + outcomeModal.id, { outcome: outcomeValue, outcome_notes: outcomeNotes }).then(function() {
      setOutcomeModal(null); setOutcomeValue(''); setOutcomeNotes('');
      toast.success('Outcome recorded');
      fetchStats(); fetchRequests(pagination.page, statusFilter);
    }).catch(function(err) {
      toast.error(extractError(err, 'Failed to record outcome. Please try again.'));
    });
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-5" data-testid="presales-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Pre-Sales Dashboard</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage requests, track SLAs, and monitor workload</p>
        </div>
        {canClaim && (
          <div className="flex items-center gap-2" data-testid="presales-self-actions">
            <button
              onClick={function() { setShowSelfCreate('demo'); }}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5"
              data-testid="schedule-demo-btn"
              title="Schedule a product demo directly — auto-assigned to you"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Schedule Demo
            </button>
            <button
              onClick={function() { setShowSelfCreate('request'); }}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 inline-flex items-center gap-1.5"
              data-testid="raise-request-btn"
              title="Raise any pre-sales request type (POC, Technical Eval, etc.)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Raise Request
            </button>
          </div>
        )}
      </div>

      {/* Iteration 78: SLA escalation ticker */}
      {slaAlerts && (slaAlerts.breached_count + slaAlerts.at_risk_count) > 0 && (
        <div
          className={'rounded-xl border-l-4 p-3 flex items-start gap-3 ' + (
            slaAlerts.breached_count > 0
              ? 'bg-red-50 dark:bg-red-900/20 border-red-500 text-red-800 dark:text-red-200'
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-800 dark:text-amber-200'
          )}
          data-testid="sla-ticker"
        >
          <span className="text-lg leading-none pt-0.5">⚠️</span>
          <div className="flex-1 text-sm">
            <p className="font-semibold">
              {slaAlerts.breached_count > 0 && <span>{slaAlerts.breached_count} request{slaAlerts.breached_count === 1 ? '' : 's'} past SLA</span>}
              {slaAlerts.breached_count > 0 && slaAlerts.at_risk_count > 0 && <span> · </span>}
              {slaAlerts.at_risk_count > 0 && <span>{slaAlerts.at_risk_count} closing in on SLA</span>}
              <span className="font-normal"> — claim or reassign before they breach.</span>
            </p>
            <div className="mt-1 flex flex-wrap gap-2">
              {(slaAlerts.breached.concat(slaAlerts.at_risk)).slice(0, 4).map(function(r) {
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={function() { setActiveTab('requests'); setStatusFilter(''); setSlaFilter('breached'); }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white/70 dark:bg-slate-800/70 rounded border border-current/10 hover:bg-white dark:hover:bg-slate-800 transition-colors"
                    data-testid={'sla-ticker-item-' + r.id}
                    title={'Urgency: ' + (r.urgency || '') + '\nAssigned: ' + (r.assigned_to_name || 'unassigned') + '\nClick to filter the list'}
                  >
                    <span className="font-medium">{(r.title || 'Untitled').slice(0, 32)}</span>
                    {r.lead_name && <span className="opacity-70">· {r.lead_name.slice(0, 18)}</span>}
                  </button>
                );
              })}
              {(slaAlerts.breached_count + slaAlerts.at_risk_count) > 4 && (
                <button
                  type="button"
                  onClick={function() { setActiveTab('requests'); setStatusFilter(''); setSlaFilter('breached'); }}
                  className="px-2 py-0.5 text-xs underline hover:no-underline"
                  data-testid="sla-ticker-more"
                >
                  + {(slaAlerts.breached_count + slaAlerts.at_risk_count) - 4} more
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label="Total" value={stats.total} testId="total"
            onClick={function() { setActiveTab('requests'); setStatusFilter(''); setSlaFilter(''); }}
            title="Show all requests" />
          <StatCard label="New" value={stats.new} color="text-blue-600" testId="new"
            onClick={function() { setActiveTab('requests'); setStatusFilter('new'); setSlaFilter(''); }}
            title="Show unassigned / new requests" />
          <StatCard label="In Progress" value={stats.in_progress} color="text-amber-600" testId="in-progress"
            onClick={function() { setActiveTab('requests'); setStatusFilter('in_progress'); setSlaFilter(''); }}
            title="Show in-progress requests" />
          <StatCard label="Completed" value={stats.completed} color="text-green-600" testId="completed"
            onClick={function() { setActiveTab('requests'); setStatusFilter('completed'); setSlaFilter(''); }}
            title="Show completed requests" />
          <StatCard label="SLA Compliance" value={stats.sla_compliance + '%'} color={stats.sla_compliance >= 80 ? 'text-green-600' : 'text-red-600'} sub={stats.sla_breached + ' breached'} testId="sla"
            onClick={function() { setActiveTab('requests'); setStatusFilter(''); setSlaFilter('breached'); }}
            title="Show SLA-breached requests" />
          <StatCard label="Conversion" value={stats.conversion_rate + '%'} color="text-purple-600" sub="completed → won" testId="conversion"
            onClick={function() { setActiveTab('workload'); }}
            title="Open workload / conversion view" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-700 p-1 rounded-lg w-fit">
        {['requests', 'workload'].map(function(t) {
          return <button key={t} onClick={function() { setActiveTab(t); }} className={'px-4 py-2 text-sm font-medium rounded-md transition-colors ' + (activeTab === t ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700')} data-testid={'tab-' + t}>{t === 'requests' ? 'Requests' : 'Workload'}</button>;
        })}
      </div>

      {activeTab === 'workload' ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden" data-testid="workload-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Engineer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Active</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Completed</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Upcoming Demos</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Load</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {workload.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No pre-sales engineers found</td></tr>
                ) : workload.map(function(w) {
                  var loadColor = w.capacity_level === 'high' ? 'bg-red-500'
                    : w.capacity_level === 'medium' ? 'bg-amber-500' : 'bg-green-500';
                  var loadLabel = w.capacity_level === 'high' ? 'Overloaded'
                    : w.capacity_level === 'medium' ? 'Near capacity' : 'Available';
                  return (
                    <tr key={w.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50" data-testid={'workload-row-' + w.id}>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
                        {w.name}
                        {(w.sla_breached > 0 || w.sla_at_risk > 0) && (
                          <div className="mt-0.5 text-[10px] font-semibold flex gap-2">
                            {w.sla_breached > 0 && <span className="text-red-600 dark:text-red-400">⚠ {w.sla_breached} breached</span>}
                            {w.sla_at_risk > 0 && <span className="text-amber-600 dark:text-amber-400">⏳ {w.sla_at_risk} at risk</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{w.role.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-center text-sm font-bold text-slate-700 dark:text-slate-200">{w.active_requests}</td>
                      <td className="px-4 py-3 text-center text-sm text-green-600">{w.completed_requests}</td>
                      <td className="px-4 py-3 text-center text-sm text-indigo-600">{w.upcoming_demos}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className={loadColor + ' h-2 rounded-full transition-all'} style={{width: (w.capacity_pct || Math.min(w.active_requests * 10, 100)) + '%'}}></div>
                          </div>
                          <span className="text-[10px] font-medium min-w-[88px] text-right" data-testid={'capacity-label-' + w.id}>
                            <span className={
                              w.capacity_level === 'high' ? 'text-red-600' :
                              w.capacity_level === 'medium' ? 'text-amber-600' : 'text-green-600'
                            }>{loadLabel}</span>
                            <span className="text-slate-400"> · {w.active_requests}/10</span>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <select value={statusFilter} onChange={function(e) { setStatusFilter(e.target.value); }} className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid="status-filter">
              <option value="">All Statuses</option>
              {Object.keys(STATUS_CONFIG).map(function(k) { return <option key={k} value={k}>{STATUS_CONFIG[k].label}</option>; })}
            </select>
            {slaFilter && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-full" data-testid="active-sla-filter">
                SLA: {slaFilter.replace('_', ' ')}
                <button type="button" onClick={function() { setSlaFilter(''); }} className="hover:text-red-900 dark:hover:text-red-100" data-testid="clear-sla-filter" title="Clear SLA filter">✕</button>
              </span>
            )}
          </div>

          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden" data-testid="requests-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Lead</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Assigned</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">SLA</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Urgency</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Outcome</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {(function() {
                    var visibleRequests = requests;
                    if (slaFilter) {
                      visibleRequests = requests.filter(function(r) {
                        var s = r.sla || {};
                        return s.response === slaFilter || s.resolution === slaFilter;
                      });
                    }
                    if (visibleRequests.length === 0) {
                      var emptyMsg = slaFilter
                        ? ('No ' + slaFilter.replace('_', ' ') + ' SLA requests found')
                        : 'No requests found';
                      return <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">{emptyMsg}</td></tr>;
                    }
                    return visibleRequests.map(function(req) {
                    var sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.new;
                    var isExpanded = !!expandedIds[req.id];
                    var toggleExpand = function() { setExpandedIds(function(prev) { var next = Object.assign({}, prev); if (next[req.id]) { delete next[req.id]; } else { next[req.id] = true; } return next; }); };
                    var hasRemarks = !!(req.requirements || req.competitor_info || req.integration_requirements || (req.activities && req.activities.length > 1));
                    return (
                      <React.Fragment key={req.id}>
                      <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50" data-testid={'request-row-' + req.id}>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <button
                              onClick={toggleExpand}
                              className={'flex-shrink-0 p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 transition-transform ' + (isExpanded ? 'rotate-90' : '')}
                              title={isExpanded ? 'Hide details' : 'Show sales notes / remarks'}
                              data-testid={'expand-btn-' + req.id}
                            >
                              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-slate-900 dark:text-white">{req.title}</span>
                              {hasRemarks && !isExpanded && (
                                <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded" title="Sales left remarks / conversation details">
                                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24"><path d="M2 5.5A3.5 3.5 0 015.5 2h13A3.5 3.5 0 0122 5.5v8a3.5 3.5 0 01-3.5 3.5H12l-4.5 4v-4H5.5A3.5 3.5 0 012 13.5v-8z"/></svg>
                                  Remarks
                                </span>
                              )}
                              {req.scheduled_date && <p className="text-[10px] text-indigo-500 mt-0.5">Scheduled: {req.scheduled_date.substring(0, 10)}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded">{(req.request_type || '').replace(/_/g, ' ')}</span></td>
                        <td className="px-4 py-3"><Link to={'/leads/' + req.lead_id} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">{req.lead_name}</Link></td>
                        <td className="px-4 py-3">
                          {req.assigned_to_name ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-700 dark:text-slate-200">{req.assigned_to_name}</span>
                              {canClaim && user && (user.role === 'admin' || user.role === 'company_sales_head' || user.role === 'solutions_architect' || req.assigned_to === user.id) && req.status !== 'completed' && req.status !== 'cancelled' && (
                                <button
                                  onClick={function() { handleReroute(req); }}
                                  className="text-[10px] px-2 py-0.5 border border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded"
                                  data-testid={'reroute-btn-' + req.id}
                                  title="Reroute this back to the queue for someone else"
                                >
                                  Reroute
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {canClaim && (
                                <button
                                  onClick={function() { handleClaim(req.id); }}
                                  className="text-xs px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded"
                                  data-testid={'claim-btn-' + req.id}
                                  title="Claim this request — self-assign and add to your task list"
                                >
                                  Claim
                                </button>
                              )}
                              <button onClick={function() { setAssignModal(req); }} className="text-xs text-blue-600 hover:underline" data-testid={'assign-btn-' + req.id}>Assign</button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3"><span className={'text-xs font-medium px-2 py-1 rounded ' + sc.color}>{sc.label}</span></td>
                        <td className="px-4 py-3"><div className="flex gap-1"><SLABadge sla={req.sla} type="response" /><SLABadge sla={req.sla} type="resolution" /></div></td>
                        <td className="px-4 py-3"><span className={'text-xs font-medium uppercase ' + (URGENCY_COLORS[req.urgency] || '')}>{req.urgency}</span></td>
                        <td className="px-4 py-3">
                          {req.outcome ? (
                            <span className={'text-xs px-2 py-1 rounded ' + (req.outcome === 'won_deal' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : req.outcome === 'lost_deal' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400')}>{req.outcome.replace(/_/g, ' ')}</span>
                          ) : req.status === 'completed' ? (
                            <button onClick={function() { setOutcomeModal(req); }} className="text-xs text-purple-600 hover:underline" data-testid={'outcome-btn-' + req.id}>Set Outcome</button>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <select value={req.status} onChange={function(e) { handleStatusChange(req.id, e.target.value); }} className="text-xs px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300" data-testid={'status-select-' + req.id}>
                            {Object.keys(STATUS_CONFIG).map(function(k) { return <option key={k} value={k}>{STATUS_CONFIG[k].label}</option>; })}
                          </select>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-slate-50 dark:bg-slate-900/40" data-testid={'request-details-' + req.id}>
                          <td colSpan={9} className="px-4 py-4">
                            <div className="pl-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="md:col-span-2">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Remarks / Requirements from Sales</p>
                                {req.requirements ? (
                                  <div className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md p-3" data-testid={'req-requirements-' + req.id}>
                                    {req.requirements}
                                  </div>
                                ) : (
                                  <p className="text-xs italic text-slate-400 dark:text-slate-500">No remarks added by sales.</p>
                                )}
                              </div>
                              {req.competitor_info && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Competitor Info</p>
                                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{req.competitor_info}</p>
                                </div>
                              )}
                              {req.integration_requirements && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Integration Requirements</p>
                                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{req.integration_requirements}</p>
                                </div>
                              )}
                              {req.solution_notes && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Solution Notes (PSE)</p>
                                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{req.solution_notes}</p>
                                </div>
                              )}
                              {req.outcome_notes && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Outcome Notes</p>
                                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{req.outcome_notes}</p>
                                </div>
                              )}
                              {Array.isArray(req.activities) && req.activities.length > 0 && (
                                <div className="md:col-span-2">
                                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Activity Log ({req.activities.length})</p>
                                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-700 max-h-52 overflow-y-auto">
                                    {req.activities.slice().reverse().map(function(a) {
                                      return (
                                        <div key={a.id} className="px-3 py-2">
                                          <div className="flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                                            <span className="font-medium text-slate-700 dark:text-slate-200">{a.user_name || 'System'}</span>
                                            <span>·</span>
                                            <span>{(a.activity_type || 'note').replace(/_/g, ' ')}</span>
                                            <span>·</span>
                                            <span>{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
                                          </div>
                                          <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap mt-0.5">{a.note}</p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <div className="md:col-span-2 flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-700">
                                <span>Requested by <span className="font-medium text-slate-700 dark:text-slate-200">{req.requested_by_name || req.requested_by || '—'}</span></span>
                                <span>·</span>
                                <span>Created {req.created_at ? new Date(req.created_at).toLocaleString() : '—'}</span>
                                {req.urgency && (<><span>·</span><span>Urgency: <span className={'font-medium uppercase ' + (URGENCY_COLORS[req.urgency] || '')}>{req.urgency}</span></span></>)}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  });
                  })()}
                </tbody>
              </table>
            </div>
            {pagination.pages > 1 && <div className="p-4 border-t border-slate-200 dark:border-slate-700"><Pagination currentPage={pagination.page} totalPages={pagination.pages} onPageChange={function(p) { fetchRequests(p, statusFilter); }} /></div>}
          </div>
        </>
      )}

      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-5" data-testid="assign-modal">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Assign Request</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{assignModal.title}</p>
            <select value={selectedAssignee} onChange={function(e) { setSelectedAssignee(e.target.value); }} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="assignee-select">
              <option value="">Select Engineer</option>
              {workload.map(function(w) {
                return <option key={w.id} value={w.id}>{w.name} ({w.role.replace(/_/g, ' ')}) — {w.active_requests} active</option>;
              })}
              {users.filter(function(u) { return !workload.find(function(w) { return w.id === u.id; }); }).map(function(u) {
                return <option key={u.id} value={u.id}>{u.name || u.email} ({u.role.replace(/_/g, ' ')})</option>;
              })}
            </select>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={function() { setAssignModal(null); }} className="px-4 py-2 text-sm border rounded-lg text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600">Cancel</button>
              <button onClick={handleAssign} disabled={!selectedAssignee} className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 disabled:opacity-50" data-testid="confirm-assign-btn">Assign</button>
            </div>
          </div>
        </div>
      )}

      {/* Outcome Modal */}
      {outcomeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-5" data-testid="outcome-modal">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Record Outcome</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{outcomeModal.title}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Outcome</label>
                <select value={outcomeValue} onChange={function(e) { setOutcomeValue(e.target.value); }} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="outcome-select">
                  <option value="">Select Outcome</option>
                  <option value="won_deal">Won Deal</option>
                  <option value="lost_deal">Lost Deal</option>
                  <option value="negotiating">Still Negotiating</option>
                  <option value="no_decision">No Decision</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
                <textarea value={outcomeNotes} onChange={function(e) { setOutcomeNotes(e.target.value); }} rows={2} placeholder="Any notes about the outcome..." className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white" data-testid="outcome-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={function() { setOutcomeModal(null); }} className="px-4 py-2 text-sm border rounded-lg text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600">Cancel</button>
              <button onClick={handleOutcome} disabled={!outcomeValue} className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 disabled:opacity-50" data-testid="save-outcome-btn">Save Outcome</button>
            </div>
          </div>
        </div>
      )}

      {showSelfCreate && (
        <SelfCreateRequestModal
          defaultType={showSelfCreate === 'demo' ? 'demo' : 'poc'}
          onClose={function() { setShowSelfCreate(null); }}
          onCreated={function() { setShowSelfCreate(null); fetchRequests(pagination.page, statusFilter); fetchStats(); fetchWorkload(); fetchSlaAlerts(); }}
        />
      )}
    </div>
  );
}
